import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { unitLabel, wasteRecency } from '@d-orca/shared/client/logistics';
import { useNow } from '@hooks/useNow';
import type { WasteEntry } from '@interfaces/ILogistics';
import { Panel } from '../Panel';
import { ExpandableList, ExpandableListEmpty, ExpandableListRow } from './ExpandableList';
import { NoMatches } from './NoMatches';
import { WasteDetail } from './WasteDetail';
import { wasteAmount, wasteNumber } from './wasteFormat';

export function WasteTracking({
  waste,
  tz,
  searchActive = false,
  now: nowOverride,
}: {
  /** One row per material, newest sync first (server order). */
  waste: WasteEntry[];
  tz: string;
  /** The Materials segment's unified search has a query — an empty list then
   *  says "no matches" instead of rendering a silently bare panel. */
  searchActive?: boolean;
  /** Injected for tests; the page never passes it. */
  now?: Date;
}) {
  const { t, i18n } = useTranslation();
  // Ticks, so Today/Yesterday roll over at the site's midnight.
  const live = useNow(60_000);
  const now = nowOverride ?? live;
  // One row open at a time: the detail is tall (tiles, chart, table), and two
  // open at once pushes the card past the viewport for no extra insight.
  const [openMaterial, setOpenMaterial] = useState<string | null>(null);
  return (
    <Panel tour="logistics-waste" title={t('logistics.waste.title')} subtitle={t('logistics.waste.subtitle')} fill>
      {waste.length === 0 ? (
        searchActive ? (
          <NoMatches />
        ) : (
          <ExpandableListEmpty>{t('logistics.waste.empty')}</ExpandableListEmpty>
        )
      ) : null}
      <ExpandableList>
        {waste.map((entry) => {
          const open = openMaterial === entry.materialName;
          return (
            <ExpandableListRow
              key={entry.materialName}
              name={entry.materialName}
              meta={t('logistics.waste.rowMeta', {
                when: wasteRecency(entry.lastSyncedAt, now, i18n.language, tz, t),
                count: entry.cycles,
              })}
              amount={wasteAmount(t, entry.totalWaste, entry.unit)}
              // What the latest sync added — the history is oldest first.
              amountSub={
                entry.history.length > 0
                  ? t('logistics.waste.lastCycle', {
                      amount: wasteNumber(entry.history[entry.history.length - 1]!.waste, entry.unit),
                      unit: unitLabel(t, entry.unit),
                    })
                  : null
              }
              open={open}
              onToggle={() => setOpenMaterial(open ? null : entry.materialName)}
              ariaLabel={t('logistics.waste.expandRow', { material: entry.materialName })}
            >
              <WasteDetail row={entry} tz={tz} />
            </ExpandableListRow>
          );
        })}
      </ExpandableList>
    </Panel>
  );
}
